import {
  collection,
  doc,
  addDoc,
  updateDoc,
  query,
  where,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "./firebase";
import type { ClubRole } from "./types";

function generateToken(): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let token = "";
  for (let i = 0; i < 32; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
}

export async function sendInvite(
  clubId: string,
  clubName: string,
  data: {
    email: string;
    role: ClubRole;
    teams: string[];
    invitedBy: string;
    invitedByName: string;
    clubLogoUrl?: string;
  }
) {
  const inviteToken = generateToken();

  // Create invite doc
  await addDoc(collection(db, "clubs", clubId, "invites"), {
    email: data.email.toLowerCase().trim(),
    role: data.role,
    teams: data.teams,
    invitedBy: data.invitedBy,
    invitedByName: data.invitedByName,
    status: "pending",
    token: inviteToken,
    clubId,
    clubName,
    createdAt: Timestamp.now(),
    acceptedAt: null,
  });

  // Send magic link email via server-side function with invite token embedded
  const sendMagicLink = httpsCallable(functions, "sendMagicLink");
  await sendMagicLink({
    email: data.email.toLowerCase().trim(),
    type: "invite",
    clubName,
    clubLogoUrl: data.clubLogoUrl || null,
    inviteToken,
  });

  return inviteToken;
}

export async function findInviteByToken(clubId: string, token: string) {
  const q = query(
    collection(db, "clubs", clubId, "invites"),
    where("token", "==", token),
    where("status", "==", "pending")
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() } as any;
}

export async function findInviteByTokenGlobal(token: string) {
  const q = query(
    collection(db, "invites"),
    where("token", "==", token),
    where("status", "==", "pending")
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() } as any;
}

export async function acceptInvite(
  inviteId: string,
  clubId: string,
  userId: string,
  displayName: string,
  email: string,
  invite: any
) {
  const { setDoc } = await import("firebase/firestore");
  await setDoc(doc(db, "clubs", clubId, "members", userId), {
    userId,
    displayName,
    email,
    role: invite.role,
    teams: invite.teams,
    clubId,
    clubName: invite.clubName,
    joinedAt: Timestamp.now(),
  });

  await updateDoc(doc(db, "clubs", clubId, "invites", inviteId), {
    status: "accepted",
    acceptedAt: Timestamp.now(),
    acceptedBy: userId,
  });
}

export async function revokeInvite(clubId: string, inviteId: string) {
  await updateDoc(doc(db, "clubs", clubId, "invites", inviteId), {
    status: "revoked",
  });
}

export async function resendInvite(clubId: string, inviteId: string, email: string, clubName: string, inviteToken: string) {
  const sendMagicLink = httpsCallable(functions, "sendMagicLink");
  await sendMagicLink({
    email,
    type: "invite",
    clubName,
    inviteToken,
  });

  await updateDoc(doc(db, "clubs", clubId, "invites", inviteId), {
    resentAt: Timestamp.now(),
  });
}
